import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle } from 'lucide-react';
import PreviewButton from '../../PreviewButton';

/**
 * DynamicMergeFields Component
 * Renders merge fields dynamically based on config and template
 * Supports two field types:
 * 1. Free text (text, free_text, string, textarea)
 * 2. DateTime (date_time, datetime) - splits into {fieldname_date} and {fieldname_time}
 */
const DynamicMergeFields = ({
  replaceFields = [],
  mergeFieldsConfig = [],
  templateData = [],
  formData,
  errors,
  handleInputChange,
  handleTemplatePreview,
  eventDateSource = null,
  isDark
}) => {
  const [showUnusualTimeWarning, setShowUnusualTimeWarning] = useState(false);

  const isUnusualTime = (datetimeValue) => {
    if (!datetimeValue) return false;
    const timePart = datetimeValue.split('T')[1];
    if (!timePart) return false;
    const hours = parseInt(timePart.split(':')[0], 10);
    return hours >= 21 || hours < 6;
  };

  // Show warning when an auto-populated event value has an unusual time
  const eventValue = formData.mergeFieldsData?.event;
  useEffect(() => {
    if (eventDateSource && eventValue && isUnusualTime(eventValue)) {
      setShowUnusualTimeWarning(true);
    }
  }, [eventValue, eventDateSource]);

  if (!replaceFields || replaceFields.length === 0) return null;
  if (!mergeFieldsConfig || mergeFieldsConfig.length === 0) return null;

  // Create a map of merge field configurations by field_name for quick lookup
  const configMap = {};
  mergeFieldsConfig.forEach(field => {
    if (field.field_name) {
      configMap[field.field_name] = field;
    }
  });

  // Find all datetime type fields
  // DateTime fields are special: they're stored as one value but displayed as {fieldname_date} and {fieldname_time} in templates
  const dateTimeFields = new Set();
  const dateTimeFieldsToExclude = new Set();

  mergeFieldsConfig.forEach(field => {
    if (field.field_type === 'date_time' || field.field_type === 'datetime') {
      const fieldName = field.field_name;
      // Check if template uses {fieldname_date} or {fieldname_time} placeholders
      const hasDatePlaceholder = replaceFields.includes(`${fieldName}_date`);
      const hasTimePlaceholder = replaceFields.includes(`${fieldName}_time`);

      if (hasDatePlaceholder || hasTimePlaceholder) {
        dateTimeFields.add(fieldName);
        // Exclude the _date and _time variants from being rendered as separate fields
        dateTimeFieldsToExclude.add(`${fieldName}_date`);
        dateTimeFieldsToExclude.add(`${fieldName}_time`);
      }
    }
  });

  // Find all free text fields (fields that exist in both template and config, excluding datetime variants)
  const freeTextFields = replaceFields.filter(fieldName => {
    // Skip datetime field variants
    if (dateTimeFieldsToExclude.has(fieldName)) {
      return false;
    }
    // Include if it exists in config
    return configMap[fieldName];
  });

  // Group fields by their label for grid layout
  const groupedFields = {};
  freeTextFields.forEach(fieldName => {
    const config = configMap[fieldName];
    if (config) {
      const label = config.field_label || fieldName;
      if (!groupedFields[label]) {
        groupedFields[label] = [];
      }
      groupedFields[label].push({ fieldName, config });
    }
  });

  /**
   * Get field value from mergeFieldsData
   */
  const getFieldValue = (fieldName) => {
    return formData.mergeFieldsData?.[fieldName] || '';
  };

  /**
   * Handle field change - updates mergeFieldsData
   */
  const handleFieldChange = (fieldName, value) => {
    handleInputChange('mergeFieldsData', {
      ...formData.mergeFieldsData,
      [fieldName]: value
    });
  };

  /**
   * Render a free text field (text input or textarea)
   */
  const renderFreeTextField = (fieldName, config) => {
    const fieldValue = getFieldValue(fieldName);
    const hasError = errors.mergeFields?.[fieldName];
    const isTextArea = config.field_type === 'textarea' || config.field_type === 'text_area';

    return (
      <>
        <div className={`box-border content-stretch flex flex-col gap-[16px] items-start p-[20px] relative rounded-[16px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] border-none w-full max-w-[822px] transition-colors duration-200 ${
          isDark ? 'bg-[#000000]' : 'bg-white border border-[#d9d9d9]'
        }`}>
          <div className="flex items-center justify-between w-full">
            <span className={`font-semibold text-[16px] leading-normal transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>
              {config.field_label || fieldName}
              {config.is_required && <span className='text-red-500'>*</span>}
            </span>
            {templateData.length > 0 && (
              <PreviewButton
                onClick={() => handleTemplatePreview(fieldName)}
                tooltip="Preview templates for this field"
                isDark={isDark}
              />
            )}
          </div>
          <div className="relative w-full">
            <div className={`border-none rounded-[8px] p-[12px] flex items-center w-full transition-colors duration-200 focus-within:border-solid focus-within:border-[1px] focus-within:border-[#CD6D2C] ${
              isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5] border-solid border-[1px] border-[#f8f9fa]'
            }`}>
              {isTextArea ? (
                <textarea
                  value={fieldValue}
                  onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                  placeholder={config.placeholder || `Enter ${config.field_label || fieldName}`}
                  rows={4}
                  className={`flex-1 bg-transparent border-0 outline-none text-[16px] resize-none transition-colors duration-200 ${
                    isDark
                      ? 'text-white placeholder:text-[#b3b3b3]'
                      : 'text-[#686868] placeholder:text-[#686868]'
                  }`}
                />
              ) : (
                <input
                  type="text"
                  value={fieldValue}
                  onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                  placeholder={config.placeholder || `Enter ${config.field_label || fieldName}`}
                  className={`flex-1 bg-transparent border-0 outline-none text-[16px] transition-colors duration-200 ${
                    isDark
                      ? 'text-white placeholder:text-[#b3b3b3]'
                      : 'text-[#686868] placeholder:text-[#686868]'
                  }`}
                />
              )}
            </div>
          </div>
        </div>
        {hasError && (
          <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-center gap-[8px] text-[14px] font-semibold text-[#FF0000]">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>You are required to fill this answer</span>
          </div>
        )}
      </>
    );
  };

  /**
   * Render a datetime field
   * Handles both event_datetime and custom datetime fields
   * Stores as single value but displays as {fieldname_date} and {fieldname_time} in templates
   */
  const renderDateTimeField = (fieldName, config) => {
    const fieldValue = getFieldValue(fieldName);
    const hasError = errors.mergeFields?.[fieldName];
    // Only lock the event field (meeting date/time), not other datetime fields
    const isLocked = fieldName === 'event' && !!eventDateSource;

    // When previewing, highlight both {fieldname_date} and {fieldname_time}
    const handlePreview = () => {
      handleTemplatePreview([`${fieldName}_date`, `${fieldName}_time`]);
    };

    return (
      <>
        <div className={`box-border content-stretch flex flex-col gap-[16px] items-start p-[20px] relative rounded-[16px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] border-none w-full max-w-[822px] transition-colors duration-200 ${
          isDark ? 'bg-[#000000]' : 'bg-white border border-[#d9d9d9]'
        }`}>
          <div className="flex items-center justify-between w-full">
            <span className={`font-semibold text-[16px] leading-normal transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>
              {config?.field_label || 'Event DateTime'}
              {(config?.is_required !== false) && <span className='text-red-500'>*</span>}
            </span>
            {templateData.length > 0 && (
              <PreviewButton
                onClick={handlePreview}
                tooltip="Preview templates for this field"
                isDark={isDark}
              />
            )}
          </div>
          <div className="relative w-full">
            <style>{`
              input[type="datetime-local"]::-webkit-calendar-picker-indicator {
                opacity: 0;
                position: absolute;
                right: 0;
                width: 100%;
                height: 100%;
                cursor: pointer;
              }
              input[type="datetime-local"]::-webkit-inner-spin-button,
              input[type="datetime-local"]::-webkit-clear-button {
                display: none;
              }
            `}</style>
            <div className={`border-none rounded-[8px] p-[12px] flex items-center w-full transition-colors duration-200 focus-within:border-solid focus-within:border-[1px] focus-within:border-[#CD6D2C] ${
              isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5] border-solid border-[1px] border-[#f8f9fa]'
            }`}>
              <input
                type="datetime-local"
                value={fieldValue || ''}
                readOnly={isLocked}
                onChange={(e) => {
                  if (isLocked) return;
                  const newValue = e.target.value || '';
                  handleFieldChange(fieldName, newValue);
                }}
                onBlur={(e) => {
                  if (isLocked) return;
                  const value = e.target.value || '';
                  if (fieldName === 'event' && isUnusualTime(value)) {
                    setShowUnusualTimeWarning(true);
                  }
                }}
                className={`flex-1 bg-transparent border-0 outline-none text-[16px] pr-[24px] transition-colors duration-200 ${
                  isDark ? 'text-white placeholder:text-[#b3b3b3]' : 'text-[#686868] placeholder:text-[#686868]'
                } ${isLocked ? 'cursor-not-allowed' : ''}`}
              />
              <div
                className="w-[16px] h-[16px] pointer-events-none shrink-0"
                style={{
                  backgroundColor: isDark ? '#b3b3b3' : '#686868',
                  WebkitMaskImage: "url('/assets/icons/calendar.svg')",
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskSize: 'contain',
                  WebkitMaskPosition: 'center',
                  maskImage: "url('/assets/icons/calendar.svg')",
                  maskRepeat: 'no-repeat',
                  maskSize: 'contain',
                  maskPosition: 'center',
                  transition: 'background-color 0.2s ease'
                }}
                aria-hidden="true"
              />
            </div>
          </div>
          {isLocked && (
            <p className={`text-[14px] font-medium transition-colors duration-200 ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>
              To change the time, please adjust it in {eventDateSource === 'strata' ? 'Strata Master' : 'Process Street'} and reselect your meeting.
            </p>
          )}
        </div>
        {hasError && (
          <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-center gap-[8px] text-[14px] font-semibold text-[#FF0000]">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>You are required to fill this answer</span>
          </div>
        )}

        {/* Unusual time warning popup */}
        {showUnusualTimeWarning && createPortal(
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center"
            style={{ zIndex: 99999 }}
            onClick={() => setShowUnusualTimeWarning(false)}
          >
            <div
              className={`rounded-[12px] p-[32px] max-w-[420px] w-full mx-4 shadow-lg flex flex-col items-center gap-[24px] transition-colors duration-200 ${
                isDark ? 'bg-[#161515]' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <img src="/assets/icons/error.svg" alt="Warning" className="w-[36px] h-[36px] shrink-0" />
              <p className={`text-[16px] font-semibold text-center m-0 transition-colors duration-200 ${
                isDark ? 'text-white' : 'text-[#072837]'
              }`}>
                {eventDateSource === 'ps'
                  ? 'Process Street has unusual event time'
                  : eventDateSource === 'strata'
                  ? 'Strata Master has unusual event time'
                  : 'Unusual event time'}
              </p>
              <p className={`text-[16px] font-normal text-center m-0 transition-colors duration-200 ${
                isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
              }`}>
                {eventDateSource === 'ps'
                  ? 'The auto-populated event time from Process Street falls outside normal hours (21:00 – 06:00). Please review before proceeding.'
                  : eventDateSource === 'strata'
                  ? 'The auto-populated event time from Strata Master falls outside normal hours (21:00 – 06:00). Please review before proceeding.'
                  : 'The event time you\'ve entered falls outside normal hours (21:00 – 06:00). Please confirm this is intentional.'}
              </p>
              <button
                className={`text-[16px] font-semibold px-[32px] py-[12px] rounded-[12px] border-0 transition-colors duration-200 cursor-pointer ${
                  isDark
                    ? 'bg-[#cd6d2c] text-[#000000] hover:bg-[#e37f3d]'
                    : 'bg-[#cd6d2c] text-white hover:bg-[#b35d24]'
                }`}
                onClick={() => setShowUnusualTimeWarning(false)}
              >
                Understood
              </button>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Render component
  return (
    <>
      {/* Render all datetime fields */}
      {Array.from(dateTimeFields).map(fieldName => {
        const config = configMap[fieldName];
        return config ? <React.Fragment key={fieldName}>{renderDateTimeField(fieldName, config)}</React.Fragment> : null;
      })}

      {/* Render grouped free text fields */}
      {Object.entries(groupedFields).map(([label, fields]) => {
        // If only one field with this label, render normally
        if (fields.length === 1) {
          const { fieldName, config } = fields[0];
          return <React.Fragment key={fieldName}>{renderFreeTextField(fieldName, config)}</React.Fragment>;
        }

        // Multiple fields with same label - render in grid
        const hasError = fields.some(({ fieldName }) => errors.mergeFields?.[fieldName]);

        return (
          <React.Fragment key={label}>
            <div className={`box-border content-stretch flex flex-col gap-[16px] items-start p-[20px] relative rounded-[16px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] border-none w-full max-w-[822px] transition-colors duration-200 ${
              isDark ? 'bg-[#000000]' : 'bg-white border border-[#d9d9d9]'
            }`}>
              <div className="flex items-center justify-between w-full">
                <span className={`font-semibold text-[16px] leading-normal transition-colors duration-200 ${
                  isDark ? 'text-white' : 'text-[#072837]'
                }`}>
                  {label}
                  {fields[0].config.is_required && <span className='text-red-500'>*</span>}
                </span>
                {templateData.length > 0 && (
                  <PreviewButton
                    onClick={() => handleTemplatePreview(fields.map(f => f.fieldName))}
                    tooltip="Preview templates for these fields"
                    isDark={isDark}
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-[16px] w-full">
                {fields.map(({ fieldName, config }) => {
                  const fieldValue = getFieldValue(fieldName);
                  const isTextArea = config.field_type === 'textarea' || config.field_type === 'text_area';

                  return (
                    <div key={fieldName} className="relative w-full">
                      <div className={`border-none rounded-[8px] p-[12px] flex items-center w-full transition-colors duration-200 focus-within:border-solid focus-within:border-[1px] focus-within:border-[#CD6D2C] ${
                        isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5] border-solid border-[1px] border-[#f8f9fa]'
                      }`}>
                        {isTextArea ? (
                          <textarea
                            value={fieldValue}
                            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                            placeholder={config.placeholder || `Enter ${config.field_label || fieldName}`}
                            rows={4}
                            className={`flex-1 bg-transparent border-0 outline-none text-[16px] resize-none transition-colors duration-200 ${
                              isDark
                                ? 'text-white placeholder:text-[#b3b3b3]'
                                : 'text-[#686868] placeholder:text-[#686868]'
                            }`}
                          />
                        ) : (
                          <input
                            type="text"
                            value={fieldValue}
                            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                            placeholder={config.placeholder || `Enter ${config.field_label || fieldName}`}
                            className={`flex-1 bg-transparent border-0 outline-none text-[16px] transition-colors duration-200 ${
                              isDark
                                ? 'text-white placeholder:text-[#b3b3b3]'
                                : 'text-[#686868] placeholder:text-[#686868]'
                            }`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {hasError && (
              <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-center gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>You are required to fill this answer</span>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default DynamicMergeFields;
